import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from 'config';

interface Review {
  _id: string;
  name: string;
  imageUrl: string;
  createdAt: string;
  order: number;
}

const Reviews = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Skeleton data for loading state
  const skeletonReviews = Array(9).fill(null);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${API_URL}/api/reviews`);
        
        // Sort reviews by order
        const sortedReviews = response.data.sort((a: Review, b: Review) => a.order - b.order);
        setReviews(sortedReviews);
      } catch (err) {
        console.error('Error fetching reviews:', err);
        // Don't set reviews to empty array on error to maintain skeleton layout
      } finally {
        setIsLoading(false);
      }
    };

    fetchReviews();
  }, []);

  const handleReviewClick = (review: Review) => {
    setSelectedReview(review);
  };

  const handleCloseModal = () => {
    setSelectedReview(null);
  };

  // Render skeleton loading state
  const renderSkeletonState = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {skeletonReviews.map((_, index) => (
        <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="aspect-w-4 aspect-h-3 bg-gray-200 animate-pulse"></div>
          <div className="p-4">
            <div className="h-5 bg-gray-200 rounded w-3/4 animate-pulse"></div>
          </div>
        </div>
      ))}
    </div>
  );

  // Render actual content
  const renderActualContent = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {reviews.map((review) => (
        <div
          key={review._id}
          className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer transform transition-transform hover:scale-[1.02]"
          onClick={() => handleReviewClick(review)}
        >
          <img
            src={`${API_URL}${review.imageUrl}`}
            alt={`Отзыв от ${review.name}`}
            className="w-full h-auto"
          />
          <div className="p-4">
            <h3 className="font-semibold text-primary-dark">{review.name}</h3>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Отзывы наших клиентов</h1>
      
      {isLoading || reviews.length === 0 ? renderSkeletonState() : renderActualContent()}

      {/* Modal */}
      {selectedReview && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={handleCloseModal}
        >
          <div
            className="relative max-w-4xl w-full bg-white rounded-lg shadow-xl my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 z-10"
              onClick={handleCloseModal}
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="max-h-[80vh] overflow-y-auto">
              <img
                src={`${API_URL}${selectedReview.imageUrl}`}
                alt={`Отзыв от ${selectedReview.name}`}
                className="w-full h-auto"
              />
              <div className="p-4 border-t">
                <h3 className="font-semibold text-lg text-primary-dark">{selectedReview.name}</h3>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reviews; 